/**
 * Poll inbound notifications, drive the unread badge, and toast new arrivals.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { notificationsApi } from "@/lib/api";
import { showInboundToast } from "@/components/shell/InboundToast";
import {
  NOTIFICATIONS_POLL_MS,
  cacheNotifications,
  computeUnreadNotificationCount,
  diffNewNotifications,
  loadCachedNotifications,
  loadSeenNotificationIds,
  saveSeenNotificationIds,
} from "@/lib/inboundNotifications";

export function useInboundNotifications({ enabled = true } = {}) {
  const navigate = useNavigate();
  const [data, setData] = useState(() => loadCachedNotifications());
  const [loading, setLoading] = useState(false);
  const bootstrapped = useRef(false);
  const seenRef = useRef(loadSeenNotificationIds());
  const openRef = useRef(null);

  const openNotification = useCallback(async (n) => {
    if (!n) return;
    const path = n.action_path || (n.conversation_id ? `/inbox/${n.conversation_id}` : "/inbox");
    try {
      if (n.id && !n.is_read) {
        const res = await notificationsApi.markRead(n.id);
        setData((prev) => {
          const next = {
            ...(prev || {}),
            notifications: (prev?.notifications || []).map((item) =>
              item.id === n.id
                ? { ...item, is_read: true, read_at: new Date().toISOString() }
                : item,
            ),
            unread_count: typeof res?.unread_count === "number"
              ? res.unread_count
              : Math.max(0, (prev?.unread_count || 1) - 1),
          };
          cacheNotifications(next);
          return next;
        });
      }
    } catch {
      // Still navigate
    }
    navigate(path);
  }, [navigate]);

  openRef.current = openNotification;

  const load = useCallback(async ({ toastNew = true } = {}) => {
    setLoading(true);
    try {
      const payload = await notificationsApi.list({ limit: 50 });
      const items = payload.notifications || [];
      const { toToast, seenIds } = diffNewNotifications(
        items,
        seenRef.current,
        { bootstrap: !bootstrapped.current },
      );
      bootstrapped.current = true;
      seenRef.current = seenIds;
      saveSeenNotificationIds(seenIds);
      setData(payload);
      cacheNotifications(payload);

      if (toastNew) {
        for (const n of toToast) {
          showInboundToast(n, (item) => {
            openRef.current?.(item);
          });
        }
      }
    } catch {
      // Keep last known data so badge doesn't flicker off
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    load({ toastNew: true });
    const id = window.setInterval(() => load({ toastNew: true }), NOTIFICATIONS_POLL_MS);
    return () => window.clearInterval(id);
  }, [enabled, load]);

  const clearOne = useCallback(async (id) => {
    const res = await notificationsApi.clear(id);
    setData((prev) => {
      const next = {
        ...(prev || {}),
        notifications: (prev?.notifications || []).filter((n) => n.id !== id),
        unread_count: typeof res?.unread_count === "number"
          ? res.unread_count
          : computeUnreadNotificationCount({
            notifications: (prev?.notifications || []).filter((n) => n.id !== id),
          }),
      };
      cacheNotifications(next);
      return next;
    });
    return res;
  }, []);

  const clearAll = useCallback(async () => {
    const res = await notificationsApi.clearAll();
    const next = { notifications: [], unread_count: 0 };
    setData(next);
    cacheNotifications(next);
    return res;
  }, []);

  const markRead = useCallback(async (id) => {
    const res = await notificationsApi.markRead(id);
    setData((prev) => {
      const next = {
        ...(prev || {}),
        notifications: (prev?.notifications || []).map((n) =>
          n.id === id ? { ...n, is_read: true, ...(res?.notification || {}) } : n,
        ),
        unread_count: typeof res?.unread_count === "number"
          ? res.unread_count
          : Math.max(0, (prev?.unread_count || 1) - 1),
      };
      cacheNotifications(next);
      return next;
    });
    return res;
  }, []);

  return {
    notifications: data?.notifications || [],
    unreadCount: computeUnreadNotificationCount(data),
    loading,
    refresh: load,
    openNotification,
    clearOne,
    clearAll,
    markRead,
  };
}
